"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AlertTriangle, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "./button";

const ConfirmDialog = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    title: string;
    description?: React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    variant?: "default" | "destructive";
    onConfirm: () => void | Promise<void>;
    loading?: boolean;
  }
>(
  (
    {
      open,
      onOpenChange,
      title,
      description,
      confirmText = "Confirmar",
      cancelText = "Cancelar",
      variant = "default",
      onConfirm,
      loading = false,
    },
    ref,
  ) => {
    const [isLoading, setIsLoading] = React.useState(false);

    const handleConfirm = async () => {
      try {
        setIsLoading(true);
        await onConfirm();
      } finally {
        setIsLoading(false);
      }
    };

    const isDestructive = variant === "destructive";
    const isPending = loading || isLoading;

    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            )}
          />
          <DialogPrimitive.Content
            ref={ref}
            className={cn(
              "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%]",
              "gap-4 border border-border/40 bg-background p-6 shadow-lg shadow-black/20",
              "duration-200 sm:rounded-xl",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            )}
          >
            <div className="flex items-start gap-4">
              {isDestructive && (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <DialogPrimitive.Title className="text-base font-semibold leading-none tracking-tight text-foreground">
                  {title}
                </DialogPrimitive.Title>
                {description && (
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                )}
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 pt-2">
              <DialogPrimitive.Close asChild>
                <Button variant="outline" type="button" disabled={isPending}>
                  {cancelText}
                </Button>
              </DialogPrimitive.Close>
              <Button
                type="button"
                variant={isDestructive ? "destructive" : "default"}
                onClick={handleConfirm}
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {confirmText}...
                  </>
                ) : (
                  confirmText
                )}
              </Button>
            </div>

            <DialogPrimitive.Close
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  },
);
ConfirmDialog.displayName = "ConfirmDialog";

export { ConfirmDialog };
